import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import getSelectedImageUuid from 'editor/src/store/design/selector/getSelectedImageUuid';
import isMultipleImageSelectAllowed from 'editor/src/store/editor/selector/isMultipleImageSelectAllowed';
import addLibraryImageToGalleryOperation from 'editor/src/store/editorModules/customerLibrary/operation/addLibraryImageToGalleryOperation';
import selectAllImagesOperation from 'editor/src/store/gallery/operation/selectAllImagesOperation';
import unselectAllImagesOperation from 'editor/src/store/gallery/operation/unselectAllImagesOperation';
import getGalleryImagesCount from 'editor/src/store/gallery/selector/getGalleryImagesCount';
import getIsAllImagesSelected from 'editor/src/store/gallery/selector/getIsAllImagesSelected';
import { selectImagesAction } from 'editor/src/store/gallery/slice';
import { GalleryImage } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import Button from 'editor/src/component/Button';

import styles from './index.module.scss';

interface Props {
  images?: GalleryImage[];
  inLibraryTab?: boolean;
}

function ButtonToggleSelectAll({ images, inLibraryTab = false }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMultiSelectEnabled, galleryImagesCount, allImagesSelected, canvasSelectedImageUuid } = useSelector(
    (state) => ({
      isMultiSelectEnabled: isMultipleImageSelectAllowed(state),
      galleryImagesCount: images?.length ?? getGalleryImagesCount(state),
      allImagesSelected: images
        ? images.every((image) => state.gallery.selectedImagesIds.includes(image.id))
        : getIsAllImagesSelected(state),
      canvasSelectedImageUuid: getSelectedImageUuid(state),
    }),
    shallowEqual,
  );

  if (!isMultiSelectEnabled || canvasSelectedImageUuid !== undefined || galleryImagesCount === 0) {
    return null;
  }

  const buttonText = allImagesSelected ? t('Unselect all') : t('Select all');

  const onClick = () => {
    if (allImagesSelected) {
      dispatch(unselectAllImagesOperation());
    } else if (images) {
      if (inLibraryTab) {
        images.forEach((image) => dispatch(addLibraryImageToGalleryOperation(image.id)));
      }
      dispatch(selectImagesAction(images.map((image) => image.id)));
    } else {
      dispatch(selectAllImagesOperation());
    }
  };

  return (
    <Button tertiary className="cy-toggle-select-all-gallery-images-button" stopPropagation onClick={onClick}>
      <span className={styles.buttonText}>{t(buttonText)}</span>
    </Button>
  );
}

export default React.memo(ButtonToggleSelectAll);
